"use client";

import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { CircleCheck as CheckCircle2, ArrowRight, ShieldAlert, Settings, Activity, Camera } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious, type CarouselApi } from "@/components/ui/carousel";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const images = [{
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1720548/img/robot_inspection_duct.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1720548/img/robot_inspection_duct.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1720548/img/robot_inspection_duct.png-wvc-sizes",
  alt: "Robot per ispezione e bonifica condotti aria interni"
}, {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1720548/img/internal_duct_cleaning_brush.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1720548/img/internal_duct_cleaning_brush.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1720548/img/internal_duct_cleaning_brush.png-wvc-sizes",
  alt: "Spazzole rotanti per trattamento interno tubazioni estrazione"
}, {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1720548/img/hvac_technical_intervention.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1720548/img/hvac_technical_intervention.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1720548/img/hvac_technical_intervention.webp-wvc-sizes",
  alt: "Intervento tecnico specializzato su impianto HVAC"
}];
const features = [{
  icon: ShieldAlert,
  title: "Il vero problema è dentro i tubi",
  desc: "Il grasso non resta solo sulla cappa, ma viene aspirato e si deposita in profondità nelle tubazioni."
}, {
  icon: Activity,
  title: "La parte visibile non basta",
  desc: "Trattare solo la cappa o i filtri non significa aver risolto il problema dei condotti interni."
}, {
  icon: Settings,
  title: "Tecnologia per tubi lunghi metri",
  desc: "I condotti si sviluppano per metri con curve e tratti verticali non raggiungibili manualmente."
}, {
  icon: Camera,
  title: "Robot e attrezzature dedicate",
  desc: "Utilizziamo robot e spazzole rotanti che entrano nel tubo per intervenire all'interno della canalizzazione."
}];
const sectors = ["Hotel", "Ristoranti", "Mense", "Supermercati", "Gastronomie", "Cucine centralizzate", "Industrie alimentari", "Strutture sanitarie", "RSA"];
export default function RoboticEquipmentShowcase() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!api) return;
    setCount(api.scrollSnapList().length);
    setCurrent(api.selectedScrollSnap() + 1);
    api.on("select", () => {
      setCurrent(api.selectedScrollSnap() + 1);
    });
  }, [api]);
  return <section id="roboticequipmentshowcase" ref={sectionRef} className="bg-background py-24 overflow-hidden">
      <div className="container mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          
          {}
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} animate={isVisible ? {
          opacity: 1,
          x: 0
        } : {}} transition={{
          duration: 0.6,
          ease: "easeOut"
        }} className="lg:col-span-5 flex flex-col space-y-8">
            <div className="space-y-4">
              <Badge variant="outline" className="font-mono text-accent border-accent/30 bg-accent/5 px-3 py-1 text-sm">
                [EQUIP_04]
              </Badge>
              <h2 className="text-3xl md:text-4xl font-bold font-default text-foreground leading-tight">
                Cappe professionali e tubazioni di estrazione cucina
              </h2>
              <p className="text-lg text-secondary-foreground/80 font-sans leading-relaxed">
                Interventi specialistici su cappe inox, filtri, plenum, tubi di estrazione, condotti interni, curve, tratti verticali, ventilatori e componenti soggetti ad accumuli di grassi, oli e vapori condensati.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {features.map((feature, i) => <div key={i} data-index={i} className="flex flex-col space-y-2">
                  <div className="flex items-center space-x-2 text-accent">
                    <feature.icon className="size-5" />
                    <h3 className="font-semibold font-default text-foreground text-base">
                      {feature.title}
                    </h3>
                  </div>
                  <p className="text-sm text-secondary-foreground/70 font-sans">
                    {feature.desc}
                  </p>
                </div>)}
            </div>

            <div className="pt-4 border-t border-border">
              <h4 className="text-sm font-semibold text-foreground mb-3 uppercase tracking-wider">Settori serviti</h4>
              <div className="flex flex-wrap gap-2">
                {sectors.map((sector, i) => <span key={i} data-index={i} className="text-xs font-medium bg-muted text-muted-foreground px-2.5 py-1 rounded-md border border-border/50">
                    {sector}
                  </span>)}
              </div>
            </div>

            <div className="bg-card border border-border rounded-xl p-6 shadow-sm mt-4 relative overflow-hidden">
              <div className="absolute top-0 left-0 w-1 h-full bg-accent"></div>
              <h4 className="text-xl font-bold font-default text-foreground mb-2">
                Vuoi sapere cosa c'è davvero dentro i tubi della tua cappa?
              </h4>
              <p className="text-sm text-secondary-foreground/80 mb-6">
                Aera esegue sopralluoghi tecnici su cappe professionali e tubazioni di estrazione cucina, con ispezione, intervento interno e documentazione finale.
              </p>
              <Button asChild size="lg" className="w-full sm:w-auto group">
                <Link to="/TechnicalInspectionPage">
                  Richiedi sopralluogo tecnico
                  <ArrowRight className="ml-2 size-4 group-hover:translate-x-1 transition-transform" />
                </Link>
              </Button>
            </div>
          </motion.div>

          {}
          <motion.div initial={{
          opacity: 0,
          x: 30
        }} animate={isVisible ? {
          opacity: 1,
          x: 0
        } : {}} transition={{
          duration: 0.6,
          ease: "easeOut",
          delay: 0.2
        }} className="lg:col-span-7 relative">
            <div className="relative rounded-2xl overflow-hidden border border-border shadow-xl bg-muted">
              {}
              <div className="absolute inset-0 bg-accent/5 pointer-events-none z-10 mix-blend-multiply"></div>
              
              <Carousel setApi={setApi} className="w-full">
                <CarouselContent>
                  {images.map((image, index) => <CarouselItem key={index} data-index={index}>
                      <div className="relative aspect-[4/3] sm:aspect-[16/10] overflow-hidden group">
                        <img src={image.src} data-wvc-srcset={image.srcset} data-wvc-sizes={image.sizes} alt={image.alt} loading={index === 0 ? "eager" : "lazy"} className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" />
                        {}
                        <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px] pointer-events-none z-20"></div>
                      </div>
                    </CarouselItem>)}
                </CarouselContent>
                
                {}
                <div className="absolute bottom-6 right-6 flex gap-2 z-30">
                  <CarouselPrevious className="relative inset-0 translate-y-0 translate-x-0 size-10 bg-background/80 backdrop-blur-sm border-border hover:bg-background hover:text-accent" />
                  <CarouselNext className="relative inset-0 translate-y-0 translate-x-0 size-10 bg-background/80 backdrop-blur-sm border-border hover:bg-background hover:text-accent" />
                </div>
              </Carousel>
            </div>

            {}
            <div className="flex justify-center gap-2 mt-6">
              {Array.from({
              length: count
            }).map((_, i) => <Button key={i} data-index={i} variant="ghost" size="sm" className={cn("h-2 rounded-full transition-all duration-300 ease-out px-0", current === i + 1 ? "w-8 bg-accent hover:bg-accent" : "w-2 bg-border hover:bg-accent/50")} onClick={() => api?.scrollTo(i)} aria-label={`{"Vai all'immagine " + (i + 1)}`} />)}
            </div>
          </motion.div>

        </div>
      </div>
    </section>;
}